
import React from 'react';
import { Target, Zap, TrendingUp, Layers } from 'lucide-react';

const ProjectCard = ({ 
  title, 
  role, 
  focus, 
  challenge, 
  solution, 
  impact, 
  stack, 
  image,
  isReversed = false 
}: { 
  title: string, 
  role: string, 
  focus: string, 
  challenge: string, 
  solution: string, 
  impact: string, 
  stack: string[], 
  image: string,
  isReversed?: boolean 
}) => (
  <div className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 mb-32 items-center`}>
    <div className="flex-1 w-full">
      <div className="relative group overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/50">
        <img 
          src={image} 
          alt={title} 
          className="w-full h-auto aspect-video object-cover transition-transform duration-700 group-hover:scale-105 opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent"></div>
        <div className="absolute bottom-6 left-6">
          <div className="text-xs font-mono text-emerald-400 mb-2 uppercase tracking-widest">{role}</div>
          <h4 className="text-2xl font-bold text-white">{title}</h4>
        </div>
      </div>
    </div>
    
    <div className="flex-1 space-y-6">
      <div className="inline-block px-3 py-1 rounded bg-slate-800 text-slate-300 text-xs font-mono uppercase">
        {focus}
      </div>
      
      <div>
        <h5 className="flex items-center gap-2 text-white font-bold mb-2">
          <Target size={18} className="text-emerald-500" /> The Challenge
        </h5>
        <p className="text-slate-400">{challenge}</p>
      </div>

      <div>
        <h5 className="flex items-center gap-2 text-white font-bold mb-2">
          <Zap size={18} className="text-emerald-500" /> The Solution
        </h5>
        <p className="text-slate-400">{solution}</p>
      </div>

      <div>
        <h5 className="flex items-center gap-2 text-white font-bold mb-2">
          <TrendingUp size={18} className="text-emerald-500" /> The Impact
        </h5>
        <p className="text-slate-400">{impact}</p>
      </div>

      <div className="pt-4">
        <div className="flex items-center gap-2 text-slate-500 text-xs font-mono uppercase mb-3">
          <Layers size={14} /> Tech Stack
        </div>
        <div className="flex flex-wrap gap-2">
          {stack.map((item, idx) => (
            <span key={idx} className="px-3 py-1 rounded-full bg-slate-800/50 border border-slate-700 text-slate-300 text-xs">
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const Projects: React.FC = () => {
  return (
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-20">
        <h2 className="text-xs font-mono text-emerald-500 uppercase tracking-[0.2em] mb-4">Case Studies</h2>
        <h3 className="text-4xl md:text-5xl font-bold text-white">Engineering Revenue.</h3>
        <p className="text-slate-400 mt-4 max-w-xl mx-auto text-lg">
          Real-world systems built to solve specific growth bottlenecks.
        </p>
      </div>
      
      <ProjectCard 
        title="SponsorScout"
        role="Lead GTM Engineer"
        focus="Automated Prospecting & Data Enrichment"
        challenge="Sponsorship teams were spending 60% of their time on manual research—Googling events, scraping LinkedIn, and guessing email addresses—leaving little time for actual selling. The 'Speed to Lead' was too slow."
        solution="I engineered an autonomous AI agent that functions as a 'Digital Strategist.' It handles automated research, reasoning about brand fit via LLMs, and generating hyper-personalized pitch emails."
        impact="Reduced research time from ~2 hours per lead to <5 minutes. Generates fully enriched, 'ready-to-send' pitch drafts at scale."
        stack={['Python', 'CrewAI', 'OpenAI API', 'Web Scraping', 'Data Enrichment']}
        image="https://picsum.photos/id/1/1200/800"
      />

      <ProjectCard 
        title="Karisma Nutrition"
        role="Automation Architect"
        focus="Lead Conversion & Personalization"
        challenge="In health-tech, generic advice kills conversion. The brand had valuable traffic, but a 'one-size-fits-all' PDF download resulted in low engagement and poor upsell rates for the coaching program."
        solution="I built a logic-based 'Triage System' that segments users based on specific symptoms. Zero-party data is captured, routed through custom automation, and delivers personalized analysis via Email/WhatsApp."
        impact="Increased conversion trust immediately. Created a perfectly segmented CRM for highly targeted future product launches."
        stack={['CrewAI', 'CRM Automation', 'Dynamic Content', 'WhatsApp API']}
        image="https://picsum.photos/id/60/1200/800"
        isReversed={true}
      />
    </div>
  );
};

export default Projects;
